import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { FaCalendarAlt, FaPrint } from "react-icons/fa";
import { useParams } from "react-router-dom";
import Layout from "../Layout";
import apiRequest from "../../../utils/vendorManager/apiRequest";
import toast from "react-hot-toast";
import { Loader } from "lucide-react";
import { getFileUrl } from "../../../utils/helper";
import { Helmet } from "react-helmet";
import useModal from "../../../hooks/ui/useModal";
import Modal from "../../ui/utils/Modal";
import InvoiceModal from "./InvoiceModal";

const OrderDetails = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isOpen, openModal, closeModal, actionType } = useModal();

  const [orderStatus, setOrderStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");

  const getOrder = async () => {
    setIsLoading(true);
    try {
      const res = await apiRequest.getOrderedDetail({ id });
      setOrder(res.data?.data || null);
      setOrderStatus(res.data?.data?.status);
      setPaymentStatus(res.data?.data?.paymentStatus);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  

  useEffect(() => {
    getOrder();
  }, []);

  const updateStatus = async () => {
    try {
      const res = await apiRequest.updateOrderStatus({
        id,
        body: {
          status: orderStatus,
          paymentStatus,
        },
      });



      if (res?.data?.response) {
        toast.success("Order Status Updated Successfully");
        getOrder();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[200px]">
        <Loader className="animate-spin w-6 h-6" />
      </div>
    );

  }



  return (
    <Layout>
      <Helmet>
        <title>Order Details</title>
      </Helmet>
      <div className="p-4 lg:p-6 bg-gray-50 min-h-screen text-[13px]">
        <h1 className="text-xl font-semibold text-gray-800 mb-4">
          Order Details
        </h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-center border-b pb-3">
              <div>
                <p className="font-semibold text-gray-800 text-base">
                  #{order.orderId}
                </p>
                <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                  <FaCalendarAlt size={12} />{" "}
                  {format(new Date(order.createdAt), "dd MMM yyyy, h:mm a")}
                </p>
              </div>

              <button
                onClick={() =>
                  openModal({ type: "edit", data: { state: order } })
                }

                className="px-2 flex items-center gap-1 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded"
              >
                <FaPrint size={12} />  Invoice
              </button>


            </div>

            <div className="mt-4 space-y-2 text-gray-700">
              <p>
                Status:{" "}
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                  Order {order?.orderStatus}
                </span>
              </p>
              <p>
                Payment:{" "}
                <span
                  className={`px-2 py-1 rounded ${paymentStatus === "Paid"
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-800"
                    }`}
                >
                  {paymentStatus || "Unpaid"}
                </span>
              </p>
              <p>Order Type: {order.paymentMethod === "razorpay" ? "Online" : "Scan & Pay" || "Online"}</p>
            </div>

            {/* PRODUCT TABLE */}
            <div className="mt-5 overflow-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="p-2 text-left">SL</th>
                    <th className="p-2 text-left">Item</th>
                    <th className="p-2 text-right">Price</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-right">Total</th>
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {order.products.map((item, index) => {
                    const p = item.productId;
                    const total = item.priceAtPurchase * item.quantity;
                    return (
                      <tr key={index}>
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2">
                          <div className="flex gap-2">
                            <img
                              src={getFileUrl(p?.image)}
                              className="w-10 h-10 rounded border"
                              alt={p?.name}
                            />
                            <div>
                              <p className="font-semibold text-gray-800">
                                {p?.name}
                              </p>
                              <p className="text-gray-500 text-[11px]">
                                ₹{item.priceAtPurchase}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-2 text-right">
                          ₹{item.priceAtPurchase}
                        </td>
                        <td className="p-2 text-right">{item.quantity}</td>
                        <td className="p-2 text-right font-semibold">
                          ₹{total}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white border rounded-lg shadow-sm p-4 h-fit">
            <h2 className="font-semibold text-gray-800 mb-3">
              Manage Order Status
            </h2>

            <label className="text-xs font-semibold">Order Status</label>
            <select
              value={orderStatus}
              onChange={(e) => setOrderStatus(e.target.value)}
              className="w-full border rounded-md p-2 mt-1 mb-3 text-xs"
            >
              {[
                "Pending",
                "Processing",
                "Dispatched",
                "Completed",
                "Cancelled",
              ].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
            <label className="text-xs font-semibold">Payment Status</label>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="w-full border rounded-md p-2 mt-1 mb-4 text-xs"
            >
              {["Paid", "Unpaid"].map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>

            <button
              onClick={updateStatus}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white py-2 rounded-md text-xs"
            >
              Update Status
            </button>
          </div>
        </div>
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} size="large">
        {actionType?.type === "edit" && (
          <InvoiceModal
            order={actionType?.data?.state}
            isOpen={isOpen}
            closeModal={closeModal}
          />
        )}

      </Modal>
    </Layout>
  );
};

export default OrderDetails;
